import type { AgentProvider } from './provider';
import { READ_ONLY_TOOLS, toolUseToCommand } from './tools';
import { serializeForAgent } from './serialize-for-agent';
import { useCanvasStore } from '../store/canvas-store';
import { boundsOf, instancesOf, selectedFrames } from '../store/selectors';
import type { Command } from '../store/types';
import { captureFramePng } from '../render/captureFrame';
import { buildNodeOutline } from './nodeOutline';
import { invalidateCanvas } from '../render/canvasInvalidate';
import { importStockAsset } from '../assets/importStockAsset';
import { errorRing } from './errorBuffer';
import { validateLottie } from '../lottie/validate';
import { listSlots } from '../lottie/resolveSlots';
import { ingestLottieJson } from '../lottie/ingest';
import { loadAssetJson } from '../lottie/loadAssetJson';
import { lottieElementsOf } from '../lottie/scan';
import type { LottieJson } from '../lottie/types';
import { applyMotionBatch, type MotionApplyInput } from './motionApply';

/** Parse a Lottie tool input (JSON string or object) and validate it. */
function parseLottieInput(v: unknown): { doc?: unknown; errors: string[] } {
  let doc: unknown = v;
  if (typeof v === 'string') {
    try { doc = JSON.parse(v); } catch { return { errors: ['lottie is not valid JSON'] }; }
  }
  const errors = validateLottie(doc);
  return errors.length ? { errors } : { doc, errors: [] };
}

/** Build the wrapping `<div data-lottie=…>` html for an ingested ref. */
function lottieElementHtml(ref: string, input: Record<string, unknown>): string {
  const w = typeof input.w === 'number' ? input.w : 240;
  const h = typeof input.h === 'number' ? input.h : w;
  const attrs = [
    `data-lottie="${ref}"`,
    input.loop === false ? 'data-lottie-loop="false"' : '',
    input.autoplay === false ? 'data-lottie-autoplay="false"' : '',
    input.slots && typeof input.slots === 'object'
      ? `data-lottie-slots='${JSON.stringify(input.slots).replace(/'/g, '&#39;')}'`
      : '',
  ].filter(Boolean).join(' ');
  return `<div ${attrs} style="width:${w}px;height:${h}px"></div>`;
}

export interface AgentStep { name: string; input: Record<string, unknown>; ok: boolean; reason?: string }

export interface ToolUseCtx {
  followCamera: boolean;
  placeOrigin?: { x: number; y: number };
  /** Mutable per-turn reposition lock; the agent loop passes one shared object
   *  across a turn's tool calls. Omit (or fresh {delta:null}) for one-off calls. */
  placeState?: { delta: { dx: number; dy: number } | null };
  onStep?: (step: AgentStep) => void;
}

/** The design frame a command mutates (for an in-turn outline refresh). */
function affectedFrameId(cmd: Command): string | null {
  switch (cmd.t) {
    case 'frame.create': return cmd.id;
    case 'frame.updateHtml': return cmd.id;
    case 'frame.editNode':
    case 'frame.setNodeMedia':
    case 'frame.deleteNode':
    case 'frame.duplicateNode':
    case 'frame.setNodeHtml':
    case 'frame.insertNode':
      return cmd.frameId;
    default: return null;
  }
}

/** Commands that introduce a new positioned frame (eligible for reposition). */
function placementCoords(cmd: Command): { x: number; y: number } | null {
  switch (cmd.t) {
    case 'frame.create':
    case 'instance.create':
    case 'note.create':
    case 'tsxFrame.create':
      return { x: cmd.x, y: cmd.y };
    case 'tsxInstance.create':
      return { x: cmd.x, y: cmd.y };
    default:
      return null;
  }
}

/** Command kinds that place or move frames — camera should follow after dispatch. */
function shouldFollowCamera(cmd: Command): boolean {
  switch (cmd.t) {
    case 'frame.create':
    case 'frame.move':
    case 'frame.resize':
    case 'instance.create':
    case 'note.create':
    case 'main.extractSubtree':
    case 'tsxFrame.create':
    case 'tsxInstance.create':
    case 'frame.editNode':
    case 'frame.setNodeMedia':
    case 'frame.deleteNode':
    case 'frame.duplicateNode':
    case 'frame.setNodeHtml':
    case 'frame.insertNode':
      return true;
    default:
      return false;
  }
}

function runReadOnlyTool(name: string, input: Record<string, unknown>): Record<string, unknown> {
  switch (name) {
    case 'find_instances': {
      const mainId = typeof input.mainId === 'string' ? input.mainId : '';
      return { ids: instancesOf(useCanvasStore.getState(), mainId) };
    }
    case 'find_tours': {
      const tours = useCanvasStore.getState().doc.tours ?? [];
      return { tours };
    }
    case 'read_comments': {
      const frameId = typeof input.frameId === 'string' ? input.frameId : undefined;
      const all = useCanvasStore.getState().doc.comments ?? [];
      const open = all.filter((t) => !t.resolved);
      const threads = frameId
        ? open.filter((t) => (t.target.kind === 'element' || t.target.kind === 'frame') && t.target.frameId === frameId)
        : open;
      return { comments: threads };
    }
    case 'get_canvas_snapshot': {
      return { snapshot: serializeForAgent(useCanvasStore.getState()) };
    }
    case 'get_errors': {
      return { errors: errorRing.list() };
    }
    default:
      return {};
  }
}

export async function executeToolUse(
  provider: AgentProvider,
  m: { id: string; name: string; input: Record<string, unknown> },
  ctx: ToolUseCtx,
): Promise<void> {
  const { followCamera, placeOrigin, placeState, onStep } = ctx;

  try {
  // Vision capture: async, handled before other tool branches.
  if (m.name === 'export_frame') {
    const frameId = typeof m.input.frameId === 'string' ? m.input.frameId : '';
    captureFramePng(frameId).then(
      (res) => { onStep?.({ name: m.name, input: m.input, ok: true }); provider.send({ type: 'tool_result', id: m.id, result: { ok: true, ...res } }); },
      (err) => { const reason = err instanceof Error ? err.message : String(err); onStep?.({ name: m.name, input: m.input, ok: false, reason }); provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } }); },
    );
    return;
  }
  // Screenshot: async PNG capture — requires frameId (captureFramePng demands one).
  if (m.name === 'screenshot') {
    const frameId = typeof m.input.frameId === 'string' ? m.input.frameId : '';
    if (!frameId) {
      onStep?.({ name: m.name, input: m.input, ok: false, reason: 'frameId is required' });
      provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: 'frameId is required' } });
      return;
    }
    captureFramePng(frameId).then(
      (res) => { onStep?.({ name: m.name, input: m.input, ok: true }); provider.send({ type: 'tool_result', id: m.id, result: { ok: true, pngBase64: res.base64, w: res.w, h: res.h } }); },
      (err) => { const reason = err instanceof Error ? err.message : String(err); onStep?.({ name: m.name, input: m.input, ok: false, reason }); provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } }); },
    );
    return;
  }
  // Stock photo: async import (re-host the remote URL) then place a frame.
  if (m.name === 'place_stock_photo') {
    const url = typeof m.input.url === 'string' ? m.input.url : '';
    const attribution = typeof m.input.attribution === 'string' ? m.input.attribution : '';
    importStockAsset(url).then(
      (id) => {
        const at = attribution.replace(/"/g, '&quot;');
        const html =
          `<img src="asset://${id}" alt="${at || 'stock photo'}"` +
          `${attribution ? ` data-attribution="${at}"` : ''}` +
          ` style="display:block;max-width:100%;height:auto" />`;
        const origin = placeOrigin ?? { x: 0, y: 0 };
        const cmd: Command = { t: 'frame.create', id: `frame_stock_${Date.now()}`, x: origin.x, y: origin.y, w: 400, title: 'Stock photo', html };
        useCanvasStore.getState().dispatch(cmd);
        invalidateCanvas();
        if (followCamera) {
          const b = boundsOf(selectedFrames(useCanvasStore.getState()));
          if (b) useCanvasStore.getState().dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
        }
        onStep?.({ name: m.name, input: m.input, ok: true });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: true, id } });
      },
      (err) => {
        const reason = err instanceof Error ? err.message : String(err);
        onStep?.({ name: m.name, input: m.input, ok: false, reason });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } });
      },
    );
    return;
  }
  // Lottie: async ingest (content-hash store the JSON) then place / swap.
  if (m.name === 'add_lottie') {
    const parsed = parseLottieInput(m.input.lottie);
    if (parsed.errors.length) {
      onStep?.({ name: m.name, input: m.input, ok: false, reason: parsed.errors[0] });
      provider.send({ type: 'tool_result', id: m.id, result: { ok: false, errors: parsed.errors } });
      return;
    }
    const frameId = typeof m.input.frameId === 'string' ? m.input.frameId : '';
    if (frameId && !(typeof m.input.refCfNodeId === 'string' && m.input.refCfNodeId)) {
      onStep?.({ name: m.name, input: m.input, ok: false, reason: 'refCfNodeId is required when inserting into an existing frame' });
      provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: 'refCfNodeId is required when inserting into an existing frame (omit frameId to create a new frame instead)' } });
      return;
    }
    ingestLottieJson(parsed.doc).then(
      (ref) => {
        const el = lottieElementHtml(ref, m.input);
        let cmd: Command;
        if (frameId) {
          const p = typeof m.input.position === 'string' ? m.input.position : 'inside';
          cmd = { t: 'frame.insertNode', frameId, refCfNodeId: typeof m.input.refCfNodeId === 'string' ? m.input.refCfNodeId : '', html: el, position: p === 'before' || p === 'after' ? p : 'inside' };
        } else {
          const origin = placeOrigin ?? { x: 0, y: 0 };
          const x = typeof m.input.x === 'number' ? m.input.x : origin.x;
          const y = typeof m.input.y === 'number' ? m.input.y : origin.y;
          cmd = { t: 'frame.create', id: `frame_lottie_${Date.now()}`, x, y, w: typeof m.input.w === 'number' ? m.input.w : 240, title: typeof m.input.title === 'string' && m.input.title ? m.input.title : 'Animation', html: el };
        }
        useCanvasStore.getState().dispatch(cmd);
        invalidateCanvas();
        if (followCamera && shouldFollowCamera(cmd)) {
          const b = boundsOf(selectedFrames(useCanvasStore.getState()));
          if (b) useCanvasStore.getState().dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
        }
        onStep?.({ name: m.name, input: m.input, ok: true });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: true, assetId: ref } });
      },
      (err) => {
        const reason = err instanceof Error ? err.message : String(err);
        onStep?.({ name: m.name, input: m.input, ok: false, reason });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } });
      },
    );
    return;
  }
  if (m.name === 'update_lottie') {
    const parsed = parseLottieInput(m.input.lottie);
    if (parsed.errors.length) {
      onStep?.({ name: m.name, input: m.input, ok: false, reason: parsed.errors[0] });
      provider.send({ type: 'tool_result', id: m.id, result: { ok: false, errors: parsed.errors } });
      return;
    }
    ingestLottieJson(parsed.doc).then(
      (ref) => {
        useCanvasStore.getState().dispatch({ t: 'frame.setNodeMedia', frameId: String(m.input.frameId ?? ''), cfNodeId: String(m.input.cfNodeId ?? ''), ref });
        invalidateCanvas();
        onStep?.({ name: m.name, input: m.input, ok: true });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: true, assetId: ref } });
      },
      (err) => {
        const reason = err instanceof Error ? err.message : String(err);
        onStep?.({ name: m.name, input: m.input, ok: false, reason });
        provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } });
      },
    );
    return;
  }
  if (m.name === 'get_lottie') {
    (async () => {
      const frameId = String(m.input.frameId ?? '');
      const f = useCanvasStore.getState().doc.frames.find((x) => x.id === frameId);
      const html = f && 'html' in f ? (f as { html: string }).html : '';
      const spec = lottieElementsOf(html).find((e) => e.cfNodeId === String(m.input.cfNodeId ?? ''));
      if (!spec) return { ok: false, error: 'no lottie element at that frameId/cfNodeId' };
      const json = (await loadAssetJson(spec.ref)) as LottieJson | null;
      if (!json) return { ok: false, error: `asset ${spec.ref} missing` };
      return { ok: true, lottie: json, slots: listSlots(json), overrides: spec.slots, loop: spec.loop, autoplay: spec.autoplay, speed: spec.speed };
    })().then(
      (result) => { onStep?.({ name: m.name, input: m.input, ok: result.ok !== false }); provider.send({ type: 'tool_result', id: m.id, result }); },
      (err) => { const reason = err instanceof Error ? err.message : String(err); onStep?.({ name: m.name, input: m.input, ok: false, reason }); provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } }); },
    );
    return;
  }
  // Batch motion: expand a whole timeline in one tool call. Sync, but sends its
  // own tool_result (with warnings) and must NOT go through toolUseToCommand.
  if (m.name === 'motion_apply') {
    const res = applyMotionBatch(m.input as unknown as MotionApplyInput);
    invalidateCanvas();
    if (res.ok) {
      const { useTimelineStore } = await import('../chrome/timeline/timeline-store');
      useTimelineStore.getState().setOpen(true);
    }
    onStep?.({ name: m.name, input: m.input, ok: res.ok, reason: res.error });
    provider.send({ type: 'tool_result', id: m.id, result: { ok: res.ok, ...(res.error ? { error: res.error } : {}), ...(res.warnings.length ? { warnings: res.warnings } : {}) } });
    return;
  }
  // Read-only tools: answer inline, never dispatch a Command.
  if (READ_ONLY_TOOLS.has(m.name)) {
    const result = runReadOnlyTool(m.name, m.input);
    onStep?.({ name: m.name, input: m.input, ok: true });
    provider.send({ type: 'tool_result', id: m.id, result: { ok: true, ...result } });
    return;
  }
  const cmd = toolUseToCommand(m.name, m.input);
  const ok = !!cmd;
  if (cmd) {
    // Reposition agent-created frames near the chat node. Lock the delta
    // on the first placement, then apply it to the rest of the turn.
    if (placeOrigin && placeState) {
      const coords = placementCoords(cmd);
      if (coords) {
        if (!placeState.delta) placeState.delta = { dx: placeOrigin.x - coords.x, dy: placeOrigin.y - coords.y };
        (cmd as { x: number; y: number }).x = coords.x + placeState.delta.dx;
        (cmd as { x: number; y: number }).y = coords.y + placeState.delta.dy;
      }
    }
    useCanvasStore.getState().dispatch(cmd);
    invalidateCanvas();
    // Agent teaches the surface: when a turn starts animating a frame, open
    // the timeline dock so the authored motion is visible + editable.
    if (cmd.t === 'motion.init') {
      const { useTimelineStore } = await import('../chrome/timeline/timeline-store');
      useTimelineStore.getState().setOpen(true);
    }
    if (followCamera && shouldFollowCamera(cmd)) {
      const target = selectedFrames(useCanvasStore.getState());
      const b = boundsOf(target.length ? target : useCanvasStore.getState().doc.frames);
      if (b) useCanvasStore.getState().dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
    }
    onStep?.({ name: m.name, input: m.input, ok });
    const fid = affectedFrameId(cmd);
    if (fid) {
      // One rAF tick lets React commit the dispatch() update to the DOM so
      // buildNodeOutline reads the post-edit [data-cf-id] tree.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      const outline = buildNodeOutline(useCanvasStore.getState(), [fid]);
      provider.send({ type: 'tool_result', id: m.id, result: outline ? { ok, outline } : { ok } });
    } else {
      provider.send({ type: 'tool_result', id: m.id, result: { ok } });
    }
    return;
  }
  onStep?.({ name: m.name, input: m.input, ok, reason: 'unknown tool name' });
  provider.send({ type: 'tool_result', id: m.id, result: { ok } });
  } catch (err) {
    // Catch-all backstop: the MCP bridge awaits tool_result by id with no
    // timeout, so a synchronous (or post-rAF) throw before send would hang it
    // forever. The async special-cases return before awaiting and handle their
    // own rejections, so they never reach here (no double-send).
    const reason = err instanceof Error ? err.message : String(err);
    onStep?.({ name: m.name, input: m.input, ok: false, reason });
    provider.send({ type: 'tool_result', id: m.id, result: { ok: false, error: reason } });
  }
}
